import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';

const PROCESSING_STEPS = [
  'Detecting item',
  'Fetching price data',
  'Checking budget impact',
];

export default function ScannerViewfinder({ isScanning, isDetected, isProcessing, productName }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [progressWidth, setProgressWidth] = useState(0);

  // Cycle subtitle text every 1.2s when processing
  useEffect(() => {
    if (!isProcessing) {
      setStepIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setStepIndex((i) => (i + 1) % PROCESSING_STEPS?.length);
    }, 1200);
    return () => clearInterval(interval);
  }, [isProcessing]);

  // Animate progress bar 0→85% over 3s when processing starts
  useEffect(() => {
    if (!isProcessing) { setProgressWidth(0); return; }
    setProgressWidth(0);
    const id = setTimeout(() => setProgressWidth(85), 60);
    return () => clearTimeout(id);
  }, [isProcessing]);

  // ── Processing / Analyzing card ──────────────────────────────────────────────
  if (isProcessing) {
    return (
      <div
        className="absolute inset-0 flex items-end justify-center pb-28 pointer-events-none"
        style={{ animation: 'fadeInProcessing 0.25s ease-out forwards' }}
      >
        <div
          className="w-[88%] max-w-sm rounded-2xl p-5"
          style={{
            background: 'rgba(26,42,58,0.92)',
            border: '1px solid #00E5FF',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
          }}
        >
          <div className="flex items-center gap-4 mb-4">
            {/* Rotating spinner */}
            <div
              style={{
                width: 32, height: 32,
                borderRadius: '50%',
                border: '2.5px solid rgba(0,229,255,0.2)',
                borderTopColor: '#00E5FF',
                flexShrink: 0,
                animation: 'spinnerRotate 1s linear infinite',
              }}
            />
            {/* Pulsing product icon */}
            <div style={{ animation: 'pulseScale 1.4s ease-in-out infinite', flexShrink: 0 }}>
              <Icon name="ShoppingBag" size={26} color="#00E5FF" strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <p className="font-heading font-bold text-white text-sm">Analyzing Product...</p>
              <p className="text-slate-400 text-xs mt-0.5" style={{ minHeight: '1rem' }}>
                {PROCESSING_STEPS?.[stepIndex]}
                <span className="ar-ellipsis">...</span>
              </p>
            </div>
          </div>
          {/* Teal progress bar 0→85% */}
          <div className="h-0.5 bg-slate-700 rounded-full overflow-hidden">
            <div
              style={{
                height: '100%',
                width: `${progressWidth}%`,
                background: 'linear-gradient(90deg, #00E5FF, #06B6D4)',
                transition: 'width 3s cubic-bezier(0.4,0,0.2,1)',
                borderRadius: 9999,
              }}
            />
          </div>
        </div>
        <style>{`
          @keyframes fadeInProcessing {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes spinnerRotate {
            to { transform: rotate(360deg); }
          }
          @keyframes pulseScale {
            0%, 100% { transform: scale(1.0); }
            50%       { transform: scale(1.15); }
          }
          .ar-ellipsis {
            display: inline-block;
            overflow: hidden;
            vertical-align: bottom;
            animation: ellipsisWidth 1.2s steps(3, end) infinite;
            width: 1.2em;
          }
          @keyframes ellipsisWidth {
            0%   { width: 0.4em; }
            33%  { width: 0.8em; }
            66%  { width: 1.2em; }
            100% { width: 0.4em; }
          }
        `}</style>
      </div>
    );
  }

  // ── Scanning frame overlay ───────────────────────────────────────────────────
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
      {/* Scanning zone: 60vw wide, 40vh tall, capped */}
      <div
        style={{
          position: 'relative',
          width: '60%',
          maxWidth: 380,
          aspectRatio: '3 / 2',
        }}
      >
        {/* Corner brackets with pulsing glow */}
        <div style={{ animation: 'bracketPulse 1.5s ease-in-out infinite' }}>
          {/* Top-left */}
          <div
            style={{
              position: 'absolute', top: 0, left: 0,
              width: 20, height: 20,
              borderTop: '2px solid #00E5FF',
              borderLeft: '2px solid #00E5FF',
            }}
          />
          {/* Top-right */}
          <div
            style={{
              position: 'absolute', top: 0, right: 0,
              width: 20, height: 20,
              borderTop: '2px solid #00E5FF',
              borderRight: '2px solid #00E5FF',
            }}
          />
          {/* Bottom-left */}
          <div
            style={{
              position: 'absolute', bottom: 0, left: 0,
              width: 20, height: 20,
              borderBottom: '2px solid #00E5FF',
              borderLeft: '2px solid #00E5FF',
            }}
          />
          {/* Bottom-right */}
          <div
            style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 20, height: 20,
              borderBottom: '2px solid #00E5FF',
              borderRight: '2px solid #00E5FF',
            }}
          />
        </div>

        {/* Animated scan line — only when actively scanning */}
        {isScanning && !isDetected && (
          <div
            style={{
              position: 'absolute',
              left: 0, right: 0,
              height: 2,
              background: 'linear-gradient(90deg, transparent 0%, #00E5FF 35%, #00E5FF 65%, transparent 100%)',
              animation: 'scanSweep 2s ease-in-out infinite',
              top: 0,
            }}
          />
        )}

        {/* Idle crosshair */}
        {!isScanning && !isDetected && (
          <div
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon name="Crosshair" size={28} color="rgba(0,229,255,0.35)" strokeWidth={1.5} />
          </div>
        )}

        {/* Detection check */}
        {isDetected && (
          <div
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <div
              style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(0,229,255,0.15)',
                border: '1px solid #00E5FF',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon name="CheckCircle" size={22} color="#00E5FF" strokeWidth={2} />
            </div>
            <span
              style={{
                color: '#00E5FF',
                fontSize: 12,
                fontWeight: 500,
                background: 'rgba(2,6,23,0.82)',
                padding: '3px 12px',
                borderRadius: 9999,
              }}
            >
              {productName || 'Product Detected'}
            </span>
          </div>
        )}
      </div>

      {/* Hint text below scanning zone */}
      {!isDetected && (
        <p
          style={{
            marginTop: 12,
            color: '#64748B',
            fontSize: 12,
            letterSpacing: '0.01em',
            textAlign: 'center',
          }}
        >
          Align product within frame
        </p>
      )}

      <style>{`
        @keyframes scanSweep {
          0%   { top: 0%;   opacity: 0; }
          8%   { opacity: 1; }
          92%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes bracketPulse {
          0%, 100% { opacity: 0.6; }
          50%      { opacity: 1.0; }
        }
      `}</style>
    </div>
  );
}